import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import toast, { Toaster } from "react-hot-toast";
import { useReservations } from "./store/ReservationsContext";
import emailjs from "emailjs-com";

// --- App ---
const App: React.FC = () => {
  const { reservations, setReservations, saveReservations } = useReservations();
  const [name, setName] = useState("");
  const [tableCount, setTableCount] = useState<number>(1);
  const [date, setDate] = useState("");
  const [activeTab, setActiveTab] = useState<"tables" | "reserve">("tables");

  // Predefined list of people for dropdown
  const peopleList = [
    "Blizanac I", "Blizanac II", "Mile", "lanMi", "GaGi", 
    "Gajdobra", "Dusan G", "Pilca", "Amerikanac", "Mateja", "Kubura"
  ];

  const emailMap: Record<string, string> = {
    "Mile": "stevicmihailo248@gmail.com",
    "Gajdobra": "dolenaci@email.com",
    "Dusan G": "dusan003gordanic@gmail.com",
    "Blizanac II": "ivanjanicevic90@gmail.com",
    "Blizanac I": "igorjanicevic33@gmail.com",
  };
  

  // Sort reservations by date (newest first) - keep all reservations
  const sortedReservations = useMemo(() => {
    return [...reservations].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [reservations]);

  const handleReservation = () => {
    if (!name || !date) {
      toast.error("Popuni sva polja!");
      return;
    }

    const newRes = {
      id: uuidv4(),
      tableNumber: 1, // Default table number since we're using table count
      tableCount,
      reservedBy: name,
      date,
      seats: [],
    };

    const next = [...reservations, newRes];
    setReservations(next);
    void saveReservations(next);
    setName("");
    setDate("");
    setTableCount(1);

    toast.success(`${tableCount} ${tableCount > 1 ? 'stolova' : 'sto'} rezervisan!`);
  };

  // Send email notification when person is added

  const sendEmailNotification = async (personName: string, dateTime: string, tableCount: number) => {
    try {
      const defaultEmail = "milkomilkic@yahoo.com";
      const toEmail = emailMap[personName] || defaultEmail;
      const htmlContent = `
        <div style="font-family: system-ui, sans-serif, Arial; font-size: 16px; background-color: #0d0d0d; color: #f5f5f5; padding: 20px;">
          <div style="max-width: 600px; margin: auto; background: rgba(25,25,25,0.85); border-radius:12px; box-shadow:0 0 20px rgba(0,255,128,0.2); padding:20px;">
            <div style="text-align:center; margin-bottom:20px;">
              <img src="https://yourdomain.com/logo.png" alt="App Arena Logo" style="height:50px; filter: drop-shadow(0 0 4px #0ff);" />
            </div>
            <h2 style="text-align:center; color:#00ffc6; margin-bottom:16px;">Nova rezervacija u MaxBet Areni!</h2>
            <p>Zdravo <strong>${personName}</strong> 👋,</p>
            <p>Rezervisan je sto za tebe i tvoju ekipu u <strong>App Areni</strong>!</p>
            <p><strong>Detalji rezervacije:</strong></p>
            <ul style="list-style:none; padding:0; margin:10px 0;">
              <li>📅 <strong>Datum i vreme:</strong> ${new Date(dateTime).toLocaleString()}</li>
              <li>🎯 <strong>Broj stolova:</strong> ${tableCount}</li>
            </ul>
            <p style="margin-top:18px;">Budi spreman, kladionica čeka! 💪<br/>Klikni ispod da otvoriš aplikaciju i potvrdiš prisustvo.</p>
            <p style="text-align:center; margin:24px 0;">
              <a href="https://app-arena-ten.vercel.app" target="_blank" 
                 style="display:inline-block; text-decoration:none; color:#0d0d0d; background-color:#00ffc6; padding:12px 24px; font-weight:bold; border-radius:8px; box-shadow:0 0 12px rgba(0,255,198,0.6);">
                Pogledaj rezervaciju
              </a>
            </p>
            <p style="font-size:14px; color:#ccc; text-align:center;">
              Ako imaš bilo kakvih pitanja, piši na 
              <a href="mailto:milkomilkic@yahoo.com" style="color:#00ffc6; text-decoration:none;">milkomilkic@yahoo.com</a>
            </p>
            <p style="text-align:center; margin-top:30px; color:#999; font-size:13px;">App Arena © 2025 — Sve prava zadržana</p>
          </div>
        </div>
      `;
  
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          to_name: personName,
          to_email: toEmail,
          email: toEmail,
          html: htmlContent,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );
  
      toast.success(`Email poslat za ${personName}!`);
    } catch (error) {
      toast.error("Neuspelo slanje emaila!");
    }
  };

  

  const addSeat = (resId: string, person: string) => {
    let next: typeof reservations = reservations;
    let reservationDate = "";
    
    setReservations((prev) => {
      next = prev.map((r) => {
        if (r.id === resId) {
          const maxSeats = (r.tableCount || 1) * 5;
          if (r.seats.length < maxSeats) {
            reservationDate = new Date(r.date).toLocaleString();
            return { ...r, seats: [...r.seats, person] };
          }
        }
        return r;
      });
      return next;
    });
    void saveReservations(next);

    if (person.trim()) {
      toast.success(`${person} dodat u sto!`);
      // Send email notification
      void sendEmailNotification(person, reservationDate, tableCount);
    }
  };

  const removeSeat = (resId: string, seatIndex: number) => {
    setReservations((prev) => {
      const next = prev.map((r) =>
        r.id === resId
          ? { ...r, seats: r.seats.filter((_, i) => i !== seatIndex) }
          : r
      );
      void saveReservations(next);
      return next;
    });
    toast.success("Osoba uklonjena!");
  };

  const deleteReservation = (resId: string) => {
    setReservations((prev) => {
      const next = prev.filter((r) => r.id !== resId);
      void saveReservations(next);
      return next;
    });
    toast.success("Rezervacija obrisana!");
  };

  return (
    <div className="app neon-bg">
      {/* Scoped styles for neon/glass/galaxy theme */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;800&family=Inter:wght@400;600;800&display=swap');

        :root {
          --bg-1: #0a0a0a;
          --bg-2: #111111;
          --card: rgba(255,255,255,0.08);
          --border: rgba(0,255,255,0.3);
          --txt: #ffffff;
          --muted: #aab4d4;
          --neon-cyan: #00ffff;
          --neon-blue: #0088ff;
          --neon-pink: #ff2ea6;
          --neon-lime: #9cff57;
          --accent: #7c4dff;
          --success: #34d399;
          --danger: #ff4d6d;
        }

        .neon-bg {
          min-height: 100vh;
          padding: 16px;
          color: var(--txt);
          font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
          background:
            radial-gradient(1200px 700px at 120% -10%, rgba(0,136,255,0.25), transparent 60%),
            radial-gradient(900px 500px at -10% 110%, rgba(0,255,255,0.18), transparent 60%),
            linear-gradient(180deg, var(--bg-1), var(--bg-2));
          position: relative;
          overflow-x: hidden;
        }

        .neon-bg::before {
          content: '';
          position: absolute;
          inset: -20% -20% -10% -20%;
          background: conic-gradient(from 0deg,
            rgba(0,255,255,0.06), rgba(0,136,255,0.06), rgba(255,46,166,0.06), rgba(0,255,255,0.06));
          filter: blur(60px);
          animation: rotateGlow 20s linear infinite;
          pointer-events: none;
        }

        @keyframes rotateGlow {
          to { transform: rotate(360deg); }
        }

        .container { max-width: 720px; margin: 0 auto; }

        .title {
          font-family: 'Orbitron', sans-serif;
          text-align: center;
          font-size: 1.75rem;
          letter-spacing: 0.02em;
          margin: 6px 0 18px 0;
          text-shadow:
            0 0 10px rgba(0,255,255,0.6),
            0 0 22px rgba(0,136,255,0.35);
        }

        .badge {
          display: inline-block;
          margin: 0 auto 10px auto;
          padding: 6px 12px;
          font-size: 0.75rem;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 999px;
          background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02));
          box-shadow: 0 0 0 1px rgba(0,136,255,0.25) inset;
          color: var(--muted);
          backdrop-filter: blur(8px);
        }

        .glass-card {
          background: linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.04));
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 14px;
          box-shadow:
            0 8px 28px rgba(0,0,0,0.35),
            0 0 0 1px rgba(255,255,255,0.06) inset,
            0 0 24px rgba(0,136,255,0.25);
          backdrop-filter: blur(14px) saturate(120%);
          overflow: hidden;
        }

        .form-grid { display: grid; gap: 10px; }

        .label { font-size: 0.8rem; color: var(--muted); margin: 4px 0 2px; }

        .input-glass {
          width: 100%;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid rgba(255,255,255,0.18);
          background: linear-gradient(180deg, rgba(14,17,27,0.55), rgba(14,17,27,0.35));
          color: var(--txt);
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease, transform .05s ease;
          box-shadow: 0 6px 18px rgba(0,0,0,0.35) inset;
          box-sizing: border-box;
        }
        /* Better calendar look */
        .input-glass::-webkit-calendar-picker-indicator {
          filter: invert(1) drop-shadow(0 0 6px rgba(0,229,255,0.8));
          cursor: pointer;
        }
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-year-field,
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-month-field,
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-day-field,
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-hour-field,
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-minute-field {
          color: var(--txt);
        }
        .input-glass[type="datetime-local"]::-webkit-datetime-edit-text { color: #7b86a8; }

        /* Tabs */
        .tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 8px 0 14px; }
        .tab {
          padding: 10px 12px;
          border-radius: 12px;
          border: 1px solid rgba(0,255,255,0.3);
          background: linear-gradient(180deg, rgba(0,255,255,0.18), rgba(0,136,255,0.12));
          color: var(--txt);
          font-weight: 800;
          letter-spacing: .02em;
          text-transform: uppercase;
          box-shadow: 0 6px 16px rgba(0,0,0,0.35);
          transition: filter .2s ease, transform .12s ease, box-shadow .2s ease;
        }
        .tab:hover { filter: brightness(1.06); box-shadow: 0 10px 22px rgba(0,136,255,0.35); }
        .tab:active { transform: translateY(1px); }
        .tab.active {
          background: linear-gradient(90deg, rgba(0,255,255,0.9), rgba(0,136,255,0.9));
          color: #001218;
          border-color: rgba(0,255,255,0.6);
        }

        .input-glass::placeholder { color: #7b86a8; }
        .input-glass:focus {
          border-color: rgba(0,255,255,0.6);
          box-shadow: 0 0 0 3px rgba(0,255,255,0.25);
        }

        .dropdown-glass {
          background: linear-gradient(180deg, rgba(14,17,27,0.55), rgba(14,17,27,0.35));
          color: var(--txt);
          cursor: pointer;
        }
        .dropdown-glass option {
          background: var(--bg-1);
          color: var(--txt);
          padding: 8px;
        }

        .btn-neon {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid rgba(0,255,255,0.4);
          color: #001218;
          background:
            linear-gradient(90deg, rgba(0,255,255,1), rgba(0,136,255,1));
          box-shadow:
            0 8px 22px rgba(0,255,255,0.35),
            0 0 18px rgba(0,136,255,0.35);
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          transition: transform .12s ease, filter .2s ease, box-shadow .2s ease;
        }

        .btn-neon:active { transform: translateY(1px) scale(0.995); }
        .btn-neon:hover {
          filter: brightness(1.05) saturate(1.1);
          box-shadow:
            0 10px 26px rgba(0,255,255,0.45),
            0 0 28px rgba(0,136,255,0.45);
        }

        .list { display: grid; gap: 12px; }

        .res-header { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .res-title { font-weight: 800; font-size: 1rem; }
        .res-title .who { color: var(--success); text-shadow: 0 0 12px rgba(52,211,153,0.4); }
        .res-date { color: var(--muted); font-size: 0.82rem; }

        .table-neon {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 0 0 1px rgba(255,255,255,0.08) inset, 0 8px 24px rgba(0,0,0,0.35);
        }
        .table-neon th, .table-neon td {
          border: 1px solid rgba(255,255,255,0.12);
          padding: 8px;
        }
        .table-neon thead th {
          background: linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04));
          color: var(--muted);
          font-weight: 600;
          text-align: left;
        }
        .table-neon tbody tr:hover td { background: rgba(0,136,255,0.07); }

        .row { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin-top: 10px; }
        .btn-secondary {
          padding: 10px 14px;
          border-radius: 12px;
          border: 1px solid rgba(0,136,255,0.35);
          background: linear-gradient(180deg, rgba(0,136,255,0.9), rgba(0,136,255,0.65));
          color: white;
          font-weight: 700;
          letter-spacing: .02em;
          box-shadow: 0 8px 22px rgba(0,136,255,0.35);
          transition: transform .12s ease, filter .2s ease, box-shadow .2s ease;
        }
        .btn-secondary:hover { filter: brightness(1.05); box-shadow: 0 10px 28px rgba(0,136,255,0.5); }
        .btn-secondary:active { transform: translateY(1px) scale(0.995); }

        .btn-delete {
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid rgba(255,77,109,0.4);
          background: linear-gradient(180deg, rgba(255,77,109,0.9), rgba(255,77,109,0.65));
          color: white;
          font-weight: 700;
          font-size: 0.8rem;
          box-shadow: 0 4px 12px rgba(255,77,109,0.35);
          transition: transform .12s ease, filter .2s ease, box-shadow .2s ease;
          cursor: pointer;
        }
        .btn-delete:hover { 
          filter: brightness(1.1); 
          box-shadow: 0 6px 16px rgba(255,77,109,0.5); 
        }
        .btn-delete:active { transform: translateY(1px) scale(0.95); }

        .btn-remove {
          padding: 4px 8px;
          border-radius: 6px;
          border: 1px solid rgba(255,77,109,0.3);
          background: linear-gradient(180deg, rgba(255,77,109,0.7), rgba(255,77,109,0.4));
          color: white;
          font-weight: 600;
          font-size: 0.7rem;
          box-shadow: 0 2px 8px rgba(255,77,109,0.25);
          transition: transform .1s ease, filter .15s ease;
          cursor: pointer;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .btn-remove:hover { 
          filter: brightness(1.2); 
          transform: scale(1.05);
        }
        .btn-remove:active { transform: scale(0.95); }

        .reservation-card {
          transition: transform .2s ease, box-shadow .2s ease;
          animation: slideIn 0.3s ease-out;
        }
        .reservation-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.4), 0 0 28px rgba(0,136,255,0.3);
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.95); }
        }

        .divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          margin: 10px 0;
        }

        .toast-neon {
          border: 1px solid rgba(255,255,255,0.18) !important;
          background: rgba(14,17,27,0.7) !important;
          color: var(--txt) !important;
          backdrop-filter: blur(10px) !important;
          box-shadow: 0 8px 28px rgba(0,0,0,0.35), 0 0 18px rgba(0,255,255,0.3) !important;
        }

        /* Mobile-first responsive design */
        @media (max-width: 480px) {
          .neon-bg { padding: 12px; }
          .title { font-size: 1.5rem; margin: 4px 0 12px 0; }
          .glass-card { padding: 12px; }
          .form-grid { gap: 8px; }
          .input-glass { padding: 10px 12px; font-size: 0.9rem; }
          .btn-neon { padding: 10px 14px; font-size: 0.9rem; }
          .table-neon th, .table-neon td { padding: 6px; font-size: 0.85rem; }
          .res-title { font-size: 0.9rem; }
          .res-date { font-size: 0.75rem; }
          .btn-delete { padding: 6px 10px; font-size: 0.75rem; }
          .btn-remove { padding: 3px 6px; font-size: 0.65rem; min-width: 20px; height: 20px; }
        }

        @media (min-width: 640px) {
          .title { font-size: 2.1rem; }
          .form-grid { grid-template-columns: 1fr 1fr; }
          .form-grid .full { grid-column: 1 / -1; }
          .container { max-width: 800px; }
        }

        @media (min-width: 768px) {
          .neon-bg { padding: 24px; }
          .glass-card { padding: 18px; }
          .form-grid { gap: 12px; }
          .table-neon th, .table-neon td { padding: 10px; }
        }
      `}</style>

      <Toaster
        position="top-center"
        toastOptions={{
          className: "toast-neon",
          style: { borderRadius: 12, padding: 12, fontWeight: 600 },
          success: { iconTheme: { primary: "#34d399", secondary: "#001b14" } },
          error: { iconTheme: { primary: "#ff4d6d", secondary: "#28000a" } },
        }}
      />

      <div className="container">
        <h1 className="title">🎲 Arena Stolovi</h1>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === "tables" ? "active" : ""}`}
            onClick={() => setActiveTab("tables")}
          >
            Stolovi
          </button>
          <button
            className={`tab ${activeTab === "reserve" ? "active" : ""}`}
            onClick={() => setActiveTab("reserve")}
          >
            Rezervacija
          </button>
        </div>

        {activeTab === "reserve" && (
          <div className="glass-card" style={{ margin: "0 auto 14px auto" }}>
            <h2
              style={{
                fontSize: "1.05rem",
                margin: "4px 0 8px",
                fontWeight: 800,
              }}
            >
              Nova rezervacija
            </h2>
            <div className="form-grid">
              <div className="full">
                <div className="label">Tvoje ime</div>
                <input
                  className="input-glass"
                  type="text"
                  placeholder="Unesi ime"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <div className="label">Broj stolova</div>
                <input
                  className="input-glass"
                  type="number"
                  min={1}
                  max={10}
                  placeholder="1"
                  value={tableCount}
                  onChange={(e) => setTableCount(Number(e.target.value))}
                />
              </div>
              <div>
                <div className="label">Datum i vreme</div>
                <input
                  className="input-glass"
                  type="datetime-local"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="full" style={{ marginTop: 4 }}>
                <button className="btn-neon" onClick={handleReservation}>
                  <span>Rezerviši sto</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "tables" && (
          <div className="list">
            {sortedReservations.map((res) => (
              <div key={res.id} className="glass-card reservation-card">
                <div className="res-header">
                  <div>
                    <div className="res-title">
                      {res.tableCount || 1} {(res.tableCount || 1) > 1 ? 'stolova' : 'sto'} –{" "}
                      <span className="who">{res.reservedBy}</span>
                    </div>
                    <div className="res-date">
                      📅 {new Date(res.date).toLocaleString()}
                    </div>
                  </div>
                  <button 
                    className="btn-delete"
                    onClick={() => deleteReservation(res.id)}
                    title="Obriši rezervaciju"
                  >
                    🗑️
                  </button>
                </div>

                <div className="divider" />

                <table className="table-neon">
                  <thead>
                    <tr>
                      <th style={{ width: 72 }}>Mesto</th>
                      <th>Ime</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: (res.tableCount || 1) * 5 }).map((_, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: "center", fontWeight: 700 }}>
                          {i + 1}
                        </td>
                        <td>{res.seats[i] || ""}</td>
                        <td style={{ textAlign: "center" }}>
                          {res.seats[i] && (
                            <button 
                              className="btn-remove"
                              onClick={() => removeSeat(res.id, i)}
                              title="Ukloni osobu"
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {res.seats.length < (res.tableCount || 1) * 5 && (
                  <SeatForm resId={res.id} addSeat={addSeat} maxSeats={(res.tableCount || 1) * 5} peopleList={peopleList} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Forma za dodavanje osobe u sto
const SeatForm: React.FC<{
  resId: string;
  addSeat: (id: string, person: string) => void;
  maxSeats: number;
  peopleList: string[];
}> = ({ resId, addSeat, maxSeats, peopleList }) => {
  const [selectedPerson, setSelectedPerson] = useState("");

  const handleAdd = () => {
    if (selectedPerson) {
      addSeat(resId, selectedPerson);
      setSelectedPerson("");
    } else {
      toast.error("Izaberi osobu!");
    }
  };

  return (
    <div className="row">
      <select
        className="input-glass dropdown-glass"
        value={selectedPerson}
        onChange={(e) => setSelectedPerson(e.target.value)}
      >
        <option value="">Izaberi osobu</option>
        {peopleList.map((person) => (
          <option key={person} value={person}>
            {person}
          </option>
        ))}
      </select>
      <button className="btn-secondary" onClick={handleAdd}>
        Dodaj
      </button>
    </div>
  );
};

export default App;
